import { useQuery } from '@tanstack/react-query';
import { saasApi, authApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';

import { isCompanyAdmin } from '@/types';

export default function TrialBanner() {
  const { role } = useAuthStore();

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => { const res = await authApi.getCompanyProfile(); return res.data.message; },
    refetchInterval: 60000,
  });

  const { data: trial } = useQuery({
    queryKey: ['trialStatus'],
    queryFn: async () => { const res = await saasApi.getTrialStatus(); return res.data.message; },
    refetchInterval: 60000,
  });

  if (!isCompanyAdmin(role)) return null;

  const inGrace = Boolean(profile?.in_grace || trial?.in_grace);
  const graceDaysLeft = profile?.grace_days_remaining ?? trial?.grace_days_remaining ?? 0;
  const retentionLeft = trial?.retention_days_remaining ?? trial?.retention_days ?? 0;

  if (inGrace) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium text-amber-800">
              Your plan has ended — grace period: {graceDaysLeft} day{graceDaysLeft === 1 ? '' : 's'} left. Renew to keep uninterrupted access.
            </span>
          </div>
          <Link to="/support" className="text-sm font-medium underline text-amber-800">
            Renew Now
          </Link>
        </div>
      </div>
    );
  }

  if (profile?.status === 'Active' && profile?.is_nearing_expiration) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium text-amber-800">
              Subscription is about to end ({profile.subscription_days_remaining} days remaining). Resubscribe to work smoothly.
            </span>
          </div>
          <Link to="/support" className="text-sm font-medium underline text-amber-800">
            Renew Now
          </Link>
        </div>
      </div>
    );
  }

  if (!trial) return null;
  if (trial.status === 'Active') return null;

  const isExpired = trial.status === 'Trial Expired' || trial.status === 'Suspended';
  const bgClass = isExpired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200';
  const textClass = isExpired ? 'text-red-800' : 'text-amber-800';
  const iconColor = isExpired ? 'text-red-500' : 'text-amber-500';

  return (
    <div className={`${bgClass} border-b px-6 py-2.5`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isExpired ? <AlertTriangle className={`w-4 h-4 ${iconColor}`} /> : <Clock className={`w-4 h-4 ${iconColor}`} />}
          <span className={`text-sm font-medium ${textClass}`}>
            {isExpired
              ? `Access blocked. Data kept for ${retentionLeft} more day${retentionLeft === 1 ? '' : 's'} — subscribe to restore.`
              : `Trial active. ${trial.days_remaining} days remaining.`}
          </span>
        </div>
        <Link to="/support" className={`text-sm font-medium underline ${textClass}`}>
          {isExpired ? 'Subscribe Now' : 'View Subscription'}
        </Link>
      </div>
    </div>
  );
}
